import { store$ } from '@redux/store';
import LoadingStatus from 'enums/loadingStatus';
import { selectFetchResultListingsStatus } from 'features/searchResultsFacets';
import { selectLoading, selectResourcesHostNames } from 'layout/redux/selectors';
import { distinctUntilChanged, filter, first, fromEvent, map } from 'rxjs';
import generateRandomNumber from 'utils/randomNumber';

/** An observable that emits the resourcesHostNames value from the Redux store whenever
 * the loading status is succeeded.
 */
const resourcesHostNames$ = store$.pipe(
  filter((state) => selectLoading(state) === LoadingStatus.Succeeded.toString()),
  map(selectResourcesHostNames),
  distinctUntilChanged()
);

/**
 * Generates a random number and returns a URL string for a lifestyle image based on that number.
 * @param imageResizeService - is a string representing the domain Image Resize Service.
 * @returns a string that represents the URL of a randomly selected lifestyle image.
 */
const getLifeStyleImage = (imageResizeService) => {
  const randomNumber = generateRandomNumber(1, 20);
  return `${imageResizeService}/images/Lifestyle/lifestyle_${randomNumber}.jpg`;
};

/**
 * Returns the URL of a placeholder image for a brand logo.
 * @param staticImagesHostName - is a string that represents the domain or URL where the resource files are located.
 */
const getBrandLogoPlaceholderImage = (staticImagesHostName) =>
  `${staticImagesHostName}/GlobalResources23/images/icons/builderLogo.png`;

/**
 * Removes all `<source>` elements within the closest `<picture>` element of a given image.
 * @param image - is a reference to an HTML `img` element.
 */
const removeSource = (image) => {
  const picture = image.closest('picture');
  if (picture) {
    picture.querySelectorAll('source').forEach((element) => element.remove());
  }
};

/**
 * Replaces the source of an image element with a placeholder image if the original image fails to load.
 * @param image - is the HTML `img` element that needs to be replaced with a new image source in case of an error.
 * @param resourcesHostNames - is an object that contains the IRS and resource domain URLs.
 */
const onErrorImage = (image, resourcesHostNames) => {
  const { staticImagesHostName, imageResizeService } = resourcesHostNames;
  removeSource(image);
  const imageSrc =
    'brandLogo' in image.dataset
      ? getBrandLogoPlaceholderImage(staticImagesHostName)
      : getLifeStyleImage(imageResizeService);
  image.src = imageSrc;
  image.onerror = null;
};

/**
 * Returns an observable that emits the first error event from a given element.
 * @param element - is the DOM element on which the `error` event is being listened to.
 */
const getErrorEvent = (element) =>
  fromEvent(element, 'error').pipe(
    map((event) => event.target),
    first()
  );

/**
 * Attaches an error event listener to lazy-loaded images and calls the `onErrorImage` function when an error occurs.
 * @param resourcesHostNames - is an object that contains onfiguration settings for handling errors in
 * lazy-loaded images.
 */
const handlerErrorInLazyImages = (resourcesHostNames) => {
  const imagesToAttachErrorEvent = document.querySelectorAll('[data-list-view-element] img:not([data-error="true"])');
  imagesToAttachErrorEvent.forEach((image) => {
    const onError$ = getErrorEvent(image);
    onError$.subscribe((imageWithError) => onErrorImage(imageWithError, resourcesHostNames));
  });
};

/**
 * Handles errors in eager-loaded images.
 * @param resourcesHostNames - is an object that contains configuration settings for handling resources,
 * such as images.
 */
const handlerErrorInEagerImages = (resourcesHostNames) => {
  const imagesWithError = document.querySelectorAll('[data-list-view-element] [data-error="true"]');
  imagesWithError.forEach((image) => onErrorImage(image, resourcesHostNames));
};

/**
 * Handles errors in both eager and lazy images.
 */
const handlerError = () => {
  resourcesHostNames$.subscribe(handlerErrorInEagerImages);
  resourcesHostNames$.subscribe(handlerErrorInLazyImages);
};

/**
 * Subscribing to the store$ observable to listen for changes.
 * When the selectFetchResultListingsStatus changes, we need to validate if the status is Succeeded
 * to handler the error of the images.
 */
store$
  .pipe(
    map(selectFetchResultListingsStatus),
    distinctUntilChanged(),
    filter((status) => status === LoadingStatus.Succeeded)
  )
  .subscribe(handlerError);

// Handler the error of the images in initial load.
handlerError();
